import { memo } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { NumericFormat } from 'react-number-format';
import { TextColor } from '@setvi/shared/enums';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { SText } from '@setvi/shared/components';
import { RowContainer, FirstRowBox, SecondRowBox } from '../styled';

interface SellColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onSellChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const useStyles = makeStyles(() => ({
  input: {
    width: '110px',
    textAlign: 'center',
    borderRadius: '99px',
    border: '1px solid #e2e2ea',
    height: '40px',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#F8F9FA',
    '& input[type=number]': {
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        display: 'none'
      }
    }
  }
}));

export const SellColumn = memo(
  ({
    product,
    productId,
    onSellChange,
    disabled = false,
    disabledTooltip = ''
  }: SellColumnProps) => {
    const classes = useStyles();

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Skeleton variant="rect" width={100} height={36} />
          </FirstRowBox>
          <SecondRowBox>
            <Skeleton variant="rect" width={100} height={26} />
          </SecondRowBox>
        </RowContainer>
      );
    }

    const isInputDisabled =
      disabled || (!product?.isEditable && !product?.isFreeHand);

    const sellInput = (
      <NumericFormat
        value={product?.sell}
        decimalScale={2}
        fixedDecimalScale
        thousandSeparator=","
        decimalSeparator="."
        prefix="$"
        onChange={e =>
          onSellChange(
            productId,
            product,
            e.target.value.replace(/[^\d.-]/g, '')
          )
        }
        style={{
          width: '100%',
          color: TextColor.Blue,
          fontWeight: 600
        }}
        disabled={isInputDisabled}
        className={classes.input}
      />
    );

    return (
      <RowContainer
        style={{
          justifyContent: 'space-between'
        }}>
        <FirstRowBox>
          {disabled && disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow>
              <Box>{sellInput}</Box>
            </Tooltip>
          ) : (
            sellInput
          )}
        </FirstRowBox>

        <SecondRowBox>
          <SText
            fontColor="#333D47"
            weight="semibold"
            style={{
              textAlign: 'center',
              width: '100%'
            }}
            title={
              product?.cost > 0
                ? product?.cost.toLocaleString('en-US', {
                    currency: 'USD',
                    style: 'currency'
                  })
                : '-'
            }
            prefix={
              <span
                style={{
                  fontWeight: 'normal'
                }}>{`Cost: `}</span>
            }
          />
        </SecondRowBox>
      </RowContainer>
    );
  }
);

SellColumn.displayName = 'SellColumn';
